import { FetchBaseQueryError } from '@reduxjs/toolkit/query/react'

import { getFirebase } from '~/firebase'

import { bootcampSanity } from '~/shared/sanity/bootcamp'

import { BootcampUserEvents, CollectedRewards, LevelActionType } from '~/bootcamp/types/bootcamp.types'

import { levelKey } from '~/bootcamp/pages/BootcampView/BootcampView.utils'

import { bootcampSplitApi } from './split.api'

const GetActivity = bootcampSplitApi.injectEndpoints({
  endpoints: (build) => ({
    getBootcampActivityData: build.query<
      BootcampUserEvents,
      { key: string; moduleKey: string; level: string; userId: number | null; language: string }
    >({
      queryFn: async ({ key, moduleKey, level, userId, language }) => {
        try {
          const result: BootcampUserEvents = {
            processedLeafs: [],
            collectedRewards: undefined,
          }
          const { getDocs, fireStore, query, collection, where } = await getFirebase()

          const userEventRef =
            userId && userId > 0
              ? getDocs(
                  query(
                    collection(fireStore, 'bootcamp', String(userId), 'events'),
                    where('bootcampId', '==', key),
                    where('moduleId', '==', moduleKey),
                  ),
                )
              : null

          const [userEvent, moduleChests] = await Promise.all([
            userEventRef,
            bootcampSanity.getBootcampGemData({ key, module: moduleKey, language }),
          ])

          let completedIds: string[] = []
          const processedData = userEvent?.docs.map((item) => {
            completedIds = [...completedIds, item.id.split('#').slice(0, 7).join('#')]
            const page = item.data()
            return {
              levelId: page.levelId,
              levelPage: page.levelPage,
              difficultyLevel: page.difficultyLevel,
              activityType: page.type,
              reward: page.reward,
              round: page.round || 0,
            }
          })
          result.completedIds = completedIds as string[]

          const firstRound = 0
          const newLeafData = moduleChests
            ?.map((chests) => {
              return chests.activities?.map((item) => {
                const availableReward = item.difficulties.reduce((acInner, inner) => acInner + inner, 0)

                const eventsOfCurrentActivitySet =
                  processedData?.filter(
                    (eventItem) =>
                      eventItem.levelId === chests.level &&
                      eventItem.activityType === item.type &&
                      eventItem.levelPage === LevelActionType.Activity,
                  ) ?? []

                const roundTotals: Record<number, number> = {}
                eventsOfCurrentActivitySet.forEach((a) => {
                  roundTotals[a.round] = (roundTotals[a.round] || 0) + a.reward
                })
                const collectedReward = Math.max(...Object.values(roundTotals), 0)
                if (collectedReward >= availableReward) {
                  return levelKey(chests.level, LevelActionType.Activity, item.type)
                }
                return ''
              })
            })
            .flatMap((actKey) => (actKey ? actKey : ''))
            ?.filter((x) => x !== '')

          if (newLeafData) {
            result.processedLeafs = result.processedLeafs?.concat(newLeafData)
          }

          result.processedLeafs = result.processedLeafs?.concat(
            processedData
              ?.filter(
                (item) => item.levelPage !== LevelActionType.Activity && item.levelPage !== LevelActionType.Round,
              )
              .map((item) => `${item.levelId}-${item.levelPage}`) ?? [],
          )

          // Here we're calculating collected rewards of current level
          result.collectedRewards =
            moduleChests
              ?.find((item) => item.level === level)
              ?.activities?.map<CollectedRewards>((item, index) => {
                const available = item.difficulties.reduce(
                  (accInnerSecond, innerSecond) => accInnerSecond + innerSecond,
                  0,
                )
                const collected =
                  processedData?.reduce((acc, eventItem) => {
                    return (
                      acc +
                      (eventItem.levelId === level &&
                      eventItem.activityType === item.type &&
                      eventItem.round === firstRound
                        ? 1
                        : 0)
                    )
                  }, 0) ?? 0
                return {
                  activityType: item.type ?? index + 1,
                  collected,
                  available,
                }
              }) ?? []
          return {
            data: result as BootcampUserEvents,
          }
        } catch (error) {
          return {
            error: error as FetchBaseQueryError,
          }
        }
      },
      providesTags: ['ActivityData'],
    }),
  }),
  overrideExisting: false,
})

export const { useGetBootcampActivityDataQuery } = GetActivity
